import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConnectionsCard } from "@/components/ConnectionsCard";

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
  "Europe/London",
];

const DELETE_CONFIRMATION = "DELETE MY ACCOUNT";

export default function Settings() {
  const { refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any>({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const deleteAccount = async () => {
    if (deleteConfirm !== DELETE_CONFIRMATION) return;
    setDeleting(true);
    try {
      await api.delete("/account", { data: { confirmation: deleteConfirm } });
      toast.success("Your account and personal data have been deleted.");
      try { await logout(); } catch { /* already gone */ }
      navigate("/");
    } catch (e) {
      toast.error(apiError(e));
      setDeleting(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/settings");
      setData(r.data);
      setForm({
        full_name: r.data.profile.full_name,
        timezone: r.data.profile.timezone,
        quiet_hours_start: r.data.profile.quiet_hours_start,
        quiet_hours_end: r.data.profile.quiet_hours_end,
        proactive_message_cap: r.data.profile.proactive_message_cap,
        always_review_on_web: r.data.profile.always_review_on_web,
      });
    } catch (err) {
      toast.error(apiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings/profile", form);
      await refreshMe();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const logoutAll = async () => {
    try {
      await api.post("/auth/logout-all");
      window.location.href = "/login";
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Settings</h1>

      <section className="rounded-3xl border border-border bg-card p-5">
        <h2 className="text-sm font-semibold">Profile</h2>
        <div className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="fn">Full name</Label>
            <Input id="fn" value={form.full_name || ""} data-testid="settings-name"
              onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={data.profile.email} disabled />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tz">Timezone</Label>
            <Select value={form.timezone || ""}
              onValueChange={(v) => setForm({ ...form, timezone: v })}>
              <SelectTrigger id="tz" data-testid="settings-timezone" className="rounded-xl">
                <SelectValue placeholder="Select your timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz} data-testid={`tz-option-${tz}`}>
                    {tz.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="qs">Quiet hours start</Label>
              <Input id="qs" value={form.quiet_hours_start || ""} data-testid="settings-quiet-start"
                onChange={(e) => setForm({ ...form, quiet_hours_start: e.target.value })} placeholder="21:00" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qe">Quiet hours end</Label>
              <Input id="qe" value={form.quiet_hours_end || ""} data-testid="settings-quiet-end"
                onChange={(e) => setForm({ ...form, quiet_hours_end: e.target.value })} placeholder="08:00" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cap">Proactive message cap (per day)</Label>
            <Input id="cap" type="number" min={0} value={form.proactive_message_cap ?? 0}
              data-testid="settings-cap"
              onChange={(e) => setForm({ ...form, proactive_message_cap: Number(e.target.value) })} />
          </div>
          <div className="flex items-center justify-between rounded-2xl bg-secondary/40 p-3">
            <div>
              <div className="text-sm font-medium">Always review on web</div>
              <div className="text-xs text-muted-foreground">Approve sensitive actions on the web page.</div>
            </div>
            <Switch checked={!!form.always_review_on_web} data-testid="settings-review-toggle"
              onCheckedChange={(v) => setForm({ ...form, always_review_on_web: v })} />
          </div>
          <Button onClick={save} className="rounded-full" disabled={saving} data-testid="settings-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </section>

      <section className="rounded-3xl border border-border bg-card p-5">
        <h2 className="text-sm font-semibold">Phone numbers</h2>
        <div className="mt-3 space-y-2">
          {data.phones.map((p: any) => (
            <div key={p.id} className="flex items-center justify-between rounded-2xl bg-secondary/40 px-4 py-3" data-testid={`phone-${p.e164_number}`}>
              <span className="font-mono text-sm">{p.e164_number}</span>
              <span className={`text-xs font-medium ${p.verified ? "text-primary" : "text-muted-foreground"}`}>
                {p.verified ? "Verified" : "Unverified"}{p.is_primary ? " · Primary" : ""}
              </span>
            </div>
          ))}
          {data.phones.length === 0 && <p className="text-sm text-muted-foreground">No phone added yet.</p>}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Changing your number requires re-verification.
        </p>
        <Link to="/onboarding">
          <Button variant="outline" className="mt-3 rounded-full" data-testid="change-number">Add / change number</Button>
        </Link>
      </section>

      <ConnectionsCard />

      <Separator />

      <Button variant="destructive" className="w-full rounded-full" onClick={logoutAll} data-testid="logout-all">
        <LogOut className="mr-2 h-4 w-4" /> Log out of all devices
      </Button>

      <Separator />

      <section
        className="rounded-3xl border border-destructive/40 bg-destructive/5 p-5"
        data-testid="danger-zone"
      >
        <h2 className="text-lg font-semibold text-destructive">Danger zone</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Deleting your account is permanent. We remove your profile, conversations, reminders,
          memories, connected Google access, and phone link. This cannot be undone.
        </p>
        <Button
          variant="destructive"
          className="mt-4 rounded-full"
          onClick={() => { setDeleteConfirm(""); setDeleteOpen(true); }}
          data-testid="delete-account-open"
        >
          <Trash2 className="mr-2 h-4 w-4" /> Delete account
        </Button>
      </section>

      <Dialog open={deleteOpen} onOpenChange={(o) => { if (!deleting) setDeleteOpen(o); }}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle>Delete your account</DialogTitle>
            <DialogDescription>
              This permanently deletes your account and all personal data. To confirm, type{" "}
              <span className="font-semibold text-foreground">{DELETE_CONFIRMATION}</span> below.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder={DELETE_CONFIRMATION}
            autoComplete="off"
            data-testid="delete-account-confirm-input"
          />
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
              data-testid="delete-account-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="rounded-full"
              onClick={deleteAccount}
              disabled={deleting || deleteConfirm !== DELETE_CONFIRMATION}
              data-testid="delete-account-confirm"
            >
              {deleting ? "Deleting…" : "Delete my account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
